<template>
  <PageChrome>
    <template #title>
      <span class="eyebrow">Contacts</span>
      <span v-if="total > 0" class="totalBadge">{{ total }}</span>
    </template>

    <template #filters>
      <input
        ref="searchInput"
        v-model="searchQuery"
        type="text"
        class="searchBox"
        placeholder="Search by name, email, or company..."
        @input="onSearchInput"
      />
      <select v-model="lifecycleFilter" class="filterSelect" @change="onFilterChange">
        <option value="">All states</option>
        <option value="none">New</option>
        <option value="staged">Staged</option>
        <option value="qualified">Qualified</option>
        <option value="outbound">Outbound</option>
        <option value="engaged">Engaged</option>
        <option value="nurture">Nurture</option>
        <option value="customer">Customer</option>
        <option value="disqualified">Disqualified</option>
        <option value="no_longer_at_company">No Longer at Company</option>
      </select>
    </template>

    <div v-if="loading && items.length === 0" class="empty"><span class="emptyText">Loading contacts...</span></div>
    <div v-else-if="items.length === 0" class="empty"><span class="emptyText">No contacts found.</span></div>
    <div v-else class="listSection">
      <div class="listHead contactGrid">
        <span class="colLabel">Contact</span>
        <span class="colLabel">Company</span>
        <span class="colLabel">Lifecycle</span>
      </div>
      <div class="memberList">
        <button
          v-for="item in items"
          :key="item.id"
          type="button"
          class="memberRow contactGrid"
          :class="{ memberRowSelected: selectedId === item.id }"
          :data-row-id="item.id"
          @click="selectContact(item)"
        >
          <HeaderWithIcon icon-key="contact" icon-color="#3b82f6" variant="stacked">
            <template #primary>
              <span class="primaryName">{{ item.name || item.email }}</span>
            </template>
            <template #secondary>
              <span class="secondaryLine">
                <span v-if="item.email">{{ item.email }}</span>
                <span v-if="item.title" class="contactTitle">({{ item.title }})</span>
              </span>
            </template>
          </HeaderWithIcon>

          <span class="companyCell">
            <FavIcon v-if="item.account_domain" :domain="item.account_domain" :size="14" />
            <span class="companyName">{{ item.account_name || item.account_domain || '—' }}</span>
          </span>

          <span class="statePill" :class="lifecyclePillClass(item.lifecycle_state)">
            {{ humanize(item.lifecycle_state) }}
          </span>

        </button>
        <div ref="sentinelEl" class="scrollSentinel" />
      </div>
      <div v-if="loading && items.length > 0" class="loadingMore">Loading...</div>
    </div>
  </PageChrome>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, onBeforeUnmount, ref } from 'vue'
import PageChrome from '../components/PageChrome.vue'
import HeaderWithIcon from '../../components/HeaderWithIcon.vue'
import FavIcon from '../../components/FavIcon.vue'
import { apiFetch } from '../../services/api'
import { useContextDrawer } from '../../drawer/useContextDrawer'
import { DRAWER_CONTEXT_CONTACT } from '../../drawer/drawerTypes'

type ContactItem = {
  id: string
  account_id: string
  account_domain?: string | null
  account_name?: string | null
  email: string
  name?: string | null
  title?: string | null
  lifecycle_state: string
  created_at?: string | null
}

const { drawerContext, setContext, openDrawer } = useContextDrawer()

const sentinelEl = ref<HTMLElement | null>(null)
let scrollObserver: IntersectionObserver | null = null

const items = ref<ContactItem[]>([])
const total = ref(0)
const nextOffset = ref<number | null>(null)
const loading = ref(false)
const searchQuery = ref('')
const lifecycleFilter = ref('')
let searchTimeout: ReturnType<typeof setTimeout> | null = null

const selectedId = computed(() => {
  const ctx = drawerContext.value
  if (ctx?.type === DRAWER_CONTEXT_CONTACT) return ctx.contactId
  return null
})

function humanize(s: string): string {
  if (!s || s === 'none') return 'New'
  return s.replace(/[_-]+/g, ' ').replace(/\b(\w)/g, m => m.toUpperCase())
}

function lifecyclePillClass(state: string): string {
  if (state === 'customer') return 'pillSuccess'
  if (state === 'engaged' || state === 'replied') return 'pillSuccess'
  if (state === 'outbound') return 'pillInfo'
  if (state === 'qualified') return 'pillInfo'
  if (state === 'staged') return 'pillWarning'
  if (state === 'nurture') return 'pillWarning'
  if (state === 'disqualified' || state === 'unsubscribed') return 'pillDanger'
  return 'pillMuted'
}

function selectContact(item: ContactItem) {
  setContext({
    type: DRAWER_CONTEXT_CONTACT,
    contactId: item.id,
    accountId: item.account_id,
  })
  openDrawer()
}

// ── Arrow key navigation ────────────────────────────────────────
function onWindowKeydown(event: KeyboardEvent) {
  if (event.defaultPrevented || event.metaKey || event.ctrlKey || event.altKey) return
  if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') return
  const target = event.target instanceof Element ? event.target : null
  if (target && target.closest('input, textarea, select, [contenteditable="true"], [role="dialog"]')) return
  const currentId = selectedId.value
  if (!currentId) return
  const idx = items.value.findIndex(i => i.id === currentId)
  if (idx < 0) return
  const next = items.value[idx + (event.key === 'ArrowDown' ? 1 : -1)]
  if (!next) return
  event.preventDefault()
  selectContact(next)
  void scrollRowIntoView(next.id)
}

async function scrollRowIntoView(id: string) {
  await nextTick()
  const el = document.querySelector(`[data-row-id="${id}"]`) as HTMLElement | null
  if (!el) return
  try { el.scrollIntoView({ block: 'nearest' }) } catch { /* */ }
}

// ── Data loading ────────────────────────────────────────────────
async function loadContacts(offset = 0) {
  loading.value = true
  try {
    const search = searchQuery.value.trim()
    const qs = new URLSearchParams({ limit: '50', offset: String(offset) })
    if (search) qs.set('q', search)
    if (lifecycleFilter.value) qs.set('lifecycle_state', lifecycleFilter.value)
    const resp = await apiFetch(`/contacts/paginated?${qs}`)
    const newItems: ContactItem[] = Array.isArray(resp?.items) ? resp.items : []
    if (offset === 0) {
      items.value = newItems
    } else {
      items.value = [...items.value, ...newItems]
    }
    total.value = resp?.total ?? 0
    nextOffset.value = resp?.next_offset ?? null
  } catch {
    if (offset === 0) items.value = []
  } finally {
    loading.value = false
  }
}

function loadMore() {
  if (loading.value) return
  if (nextOffset.value == null) return
  void loadContacts(nextOffset.value)
}

function onSearchInput() {
  if (searchTimeout) clearTimeout(searchTimeout)
  searchTimeout = setTimeout(() => { void loadContacts(0) }, 300)
}

function onFilterChange() {
  void loadContacts(0)
}

function setupScrollObserver() {
  scrollObserver?.disconnect()
  if (sentinelEl.value) {
    scrollObserver = new IntersectionObserver(
      (entries) => { if (entries[0]?.isIntersecting) loadMore() },
      { rootMargin: '200px', threshold: 0 },
    )
    scrollObserver.observe(sentinelEl.value)
  }
}

onMounted(async () => {
  await loadContacts(0)
  await nextTick()
  setupScrollObserver()
  window.addEventListener('keydown', onWindowKeydown)
})
onBeforeUnmount(() => {
  window.removeEventListener('keydown', onWindowKeydown)
  scrollObserver?.disconnect()
  if (searchTimeout) clearTimeout(searchTimeout)
})
</script>

<style scoped>
.eyebrow {
  font-family: var(--font-sans);
  font-size: var(--fs-xs, 12px);
  font-weight: var(--fw-bold, 700);
  text-transform: uppercase;
  letter-spacing: var(--tracking-wide, 0.06em);
  color: var(--color-text-tertiary);
}

.totalBadge {
  margin-left: var(--space-2, 8px);
  font-size: 11px;
  font-weight: 700;
  color: var(--color-text-tertiary);
  background: var(--color-bg-app);
  border-radius: var(--radius-full);
  padding: 1px 7px;
}

.searchBox {
  width: 280px;
  padding: 6px 12px;
  font-size: 13px;
  border: 1px solid var(--color-border, #e5e2dc);
  border-radius: var(--radius-md, 8px);
  background: var(--color-bg-surface, #fff);
  color: var(--color-text-primary);
  outline: none;
  transition: border-color 150ms ease;
}
.searchBox:focus {
  border-color: var(--color-accent, #059669);
}

.filterSelect {
  padding: 6px 12px;
  font-size: 13px;
  border: 1px solid var(--color-border, #e5e2dc);
  border-radius: var(--radius-md, 8px);
  background: var(--color-bg-surface, #fff);
  color: var(--color-text-primary);
  outline: none;
  cursor: pointer;
}
.filterSelect:focus {
  border-color: var(--color-accent, #059669);
}

/* ── Grid ────────────────────────────────────────────────────────── */
.contactGrid {
  display: grid;
  grid-template-columns: minmax(0, 1.5fr) minmax(0, 1fr) 110px;
  gap: 12px;
  align-items: center;
}

.listHead {
  padding: 6px 12px;
}

.colLabel {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--color-text-tertiary, #a3a3a3);
}

.memberList {
  display: flex;
  flex-direction: column;
}

.memberRow {
  padding: 8px 12px;
  border-top: 1px solid var(--color-border, #e5e2dc);
  background: transparent;
  border-left: 0;
  border-right: 0;
  border-bottom: 0;
  cursor: pointer;
  text-align: left;
  font: inherit;
  color: inherit;
  outline: none;
  transition: background 100ms ease;
}
.memberRow:hover {
  background: var(--color-bg-subtle, #f2ede5);
}
.memberRow:focus-visible {
  outline: none;
}
.memberRowSelected {
  background: var(--color-bg-subtle, #f2ede5);
}
.memberRowSelected:hover {
  background: var(--color-bg-subtle, #f2ede5);
}

.primaryName {
  font-weight: 700;
  color: var(--color-text-primary, var(--text));
}

.secondaryLine {
  display: flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
}

.contactTitle {
  color: var(--color-text-tertiary, var(--muted));
}

.companyCell {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
}

.companyName {
  font-size: 13px;
  color: var(--color-text-secondary, #525252);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* ── Pills ───────────────────────────────────────────────────────── */
.statePill {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  line-height: 1.4;
  white-space: nowrap;
  justify-self: start;
}
.pillSuccess { background: var(--color-success-soft, #ecfdf5); color: var(--color-success, #047857); }
.pillInfo { background: var(--color-info-soft, #eff6ff); color: var(--color-info, #1d4ed8); }
.pillWarning { background: var(--color-warning-soft, #fff7ed); color: var(--color-warning, #c2410c); }
.pillDanger { background: var(--color-danger-soft, #fef2f2); color: var(--color-danger, #b91c1c); }
.pillMuted { background: var(--color-muted-soft, #f5f5f5); color: var(--color-muted, #737373); }

.scrollSentinel {
  height: 1px;
}

.loadingMore {
  text-align: center;
  font-size: 12px;
  color: var(--color-text-tertiary);
  padding: 8px;
}

.empty {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--space-8, 56px);
}

.emptyText {
  font-size: 14px;
  color: var(--color-text-tertiary, #a3a3a3);
}
</style>
